import React from 'react';
import { Phone, FileText, Mail } from "lucide-react";
import { createPageUrl } from "@/utils";

export default function HeroSection() {
  return (
    <section id="home" className="relative min-h-screen flex items-center bg-[#080808] overflow-hidden w-full">
      {/* Full-bleed background image */}
      <img
        src="/images/optimized/2d4e6336b_IMG_5554.webp"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: 'center 45%' }}
        fetchPriority="high"
        decoding="sync"
        width="1920"
        height="1080"
      />

      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-[#080808]/70" />
      {/* Extra bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#080808] to-transparent w-full" />

      {/* Subtle blue glow behind text */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[600px] h-[500px] bg-[#1e3a5f]/10 rounded-full blur-[100px] pointer-events-none hidden md:block" />

      {/* Content */}
      <div className="relative z-10 w-full py-28 md:py-0 md:min-h-screen md:flex md:items-center px-5 sm:px-10 md:pl-24 lg:pl-32 md:pr-10">
        <div className="w-full max-w-2xl">

          {/* Label */}
          <p className="text-[#60a5fa] text-xs font-semibold tracking-[0.25em] uppercase mb-5">
            Brisbane &amp; South East Queensland
          </p>

          {/* Heading */}
          <h1 style={{ fontSize: 'clamp(1.9rem, 7vw, 3.6rem)', wordBreak: 'break-word', overflowWrap: 'break-word' }} className="font-bold leading-[1.1] mb-5 tracking-tight">
            <span className="bg-gradient-to-r from-[#d0d0d0] via-[#ffffff] to-[#c0c0c0] bg-clip-text text-transparent">
              Commercial Plumbing Brisbane &amp; South East Queensland
            </span>
          </h1>
          <p className="text-white text-lg md:text-xl font-semibold mb-4">The commercial plumbers that builders rely on.</p>

          {/* Subheading */}
          <p className="text-gray-400 text-base md:text-lg font-light leading-relaxed mb-8 max-w-lg" style={{ fontSize: 'clamp(0.95rem, 3.5vw, 1.1rem)' }}>
            30+ years of plumbing experience delivering compliant plumbing for shopfits, commercial builds and other projects.
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm sm:max-w-none">
            <a href={createPageUrl('RequestAQuote')} className="w-full sm:w-auto">
              <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-all duration-300 hover:shadow-xl hover:shadow-[#2563eb]/25 hover:-translate-y-0.5 rounded-sm btn-sweep">
                <FileText className="w-4 h-4" />
                Request a Quote
              </button>
            </a>
            <a href="tel:0403448370" className="w-full sm:w-auto">
              <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-gray-300 hover:text-white bg-black/30 hover:bg-white/5 px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-all duration-300 rounded-sm">
                <Phone className="w-4 h-4 text-[#2563eb]" />
                0403 448 370
              </button>
            </a>
            <a href="mailto:info@oliverandsons.com.au" className="w-full sm:w-auto">
              <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-gray-300 hover:text-white bg-black/30 hover:bg-white/5 px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-all duration-300 rounded-sm whitespace-nowrap">
                <Mail className="w-4 h-4 text-[#2563eb] flex-shrink-0" />
                info@oliverandsons.com.au
              </button>
            </a>
          </div>

          {/* Credential pills */}
          <div className="flex flex-wrap gap-2 mt-10 w-full">
            {[
              "QBCC Lic. 15538687",
              "Fully Insured",
              "Brisbane & SEQ",
              "Commercial Plumbing"
            ].map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#080808]/70 border border-[#2563eb]/25 text-gray-400 text-xs font-medium tracking-wide rounded-full"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb] flex-shrink-0" />
                {badge}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
